import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, FormControl, InputGroup, Spinner, Alert } from 'react-bootstrap';
import L from 'leaflet';
import { FaMapMarkerAlt, FaSearch } from 'react-icons/fa';

import 'leaflet/dist/leaflet.css';
import FieldCity from './FieldCity';
import FieldState from './FieldState';
import FieldCountry from './FieldCountry';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const GOOGLE_API_KEY = 'AIzaSyBIqXz1A8OhSJuDvAhRNpbjIwfVyNvfFsU';

// Load Google Maps API
const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });
};

const getCityByName = (cityName) => {
  try {
    if (FieldCity.getCityByName) {
      return FieldCity.getCityByName(cityName);
    }
    return null;
  } catch (error) {
    return null;
  }
};

const normalizeCityName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/^(greater\s+)?(mumbai|delhi|kolkata|chennai)\s*(city)?$/i, '$2')
    .replace(/\s*(division|district|city|town|village)$/i, '')
    .trim();
};

const FieldLocation = ({ id, field, value, onValueChange, editable = true, visible = true }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  
  const searchTimeoutRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Load Google Maps API on component mount
  useEffect(() => {
    loadGoogleMapsAPI()
      .then(() => {
        setGoogleMapsLoaded(true);
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
      })
      .catch(error => console.error('Failed to load Google Maps:', error));
  }, []);

  useEffect(() => {
    if (!value) return;

    if (typeof value === 'string') {
      const parts = value.split('~').filter(p => p);
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        const address = parts[2] || '';
        setSelectedPosition({ lat, lng, address });
      }
      return;
    }

    if (typeof value === 'object') {
      const city = typeof value.city === 'object'
        ? value.city
        : FieldCity.getCityById(value.city);
      const state = typeof value.state === 'object'
        ? value.state
        : FieldState.getStateById(value.state);
      const country = typeof value.country === 'object'
        ? value.country
        : FieldCountry.getCountryById(value.country);

      const positionData = {
        lat: value.lat,
        lng: value.lng,
        street: value.street || '',
        city,
        state,
        country,
        pin: value.pin || '',
        address: value.address || value.street || ''
      };
      
      setSelectedPosition(positionData);
    }
  }, [value]);

  // Search using Google Places Autocomplete
  const searchLocation = async (query) => {
    if (!query.trim() || !autocompleteServiceRef.current) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      const request = {
        input: query,
        componentRestrictions: { country: 'in' },
        types: ['geocode', 'establishment']
      };

      autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
        setIsSearching(false);
      });
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        searchLocation(query);
      }, 500);
    } else {
      setSearchResults([]);
    }
  };

  // Get place details and set location
  const handleSelectSearchResult = async (prediction) => {
    if (!geocoderRef.current) return;

    try {
      geocoderRef.current.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          const lat = result.geometry.location.lat();
          const lng = result.geometry.location.lng();

          const addressComponents = {};
          result.address_components.forEach(component => {
            component.types.forEach(type => {
              addressComponents[type] = component.long_name;
            });
          });

          // Check if in India
          if (addressComponents.country !== "India") {
            setLocationError("Selected location is outside India");
            return;
          }

          // Find matching city
          let city = null;
          const possibleCityNames = [
            addressComponents.locality,
            addressComponents.administrative_area_level_2,
            addressComponents.administrative_area_level_3
          ].filter(Boolean);

          for (const cityName of possibleCityNames) {
            city = getCityByName(cityName) || getCityByName(normalizeCityName(cityName));
            if (city) break;
          }

          let state = null;
          let country = null;
          let missingFields = [];

          if (city) {
            state = FieldState.getStateById(city.state_id);
          }

          if (state) {
            country = FieldCountry.getCountryById(state.country_id);
          } else {
            country = { label: 'India', value: 'India' };
          }

          if (!city) missingFields.push('city');
          if (!state) missingFields.push('state');

        const placeName = prediction.structured_formatting?.main_text;

        const streetParts = [
          placeName,
          addressComponents.premise,
          addressComponents.street_number,
          addressComponents.route,
          addressComponents.sublocality_level_2,
          addressComponents.sublocality_level_1,
          addressComponents.sublocality
        ].filter(Boolean);


          const selectedData = {
            lat,
            lng,
            street: streetParts.join(', '),
            city,
            state,
            country,
            pin: addressComponents.postal_code || '',
            address: result.formatted_address
          };

          setSelectedPosition(selectedData);

          if (missingFields.length > 0) {
            setLocationError(`Could not find matching ${missingFields.join(', ')} in database. Please select manually.`);
          } else {
            setLocationError('');
          }

          onValueChange(id, selectedData);

          if (mapRef.current) {
            mapRef.current.setView([lat, lng], 15);
            if (markerRef.current) {
              mapRef.current.removeLayer(markerRef.current);
            }
            const newMarker = L.marker([lat, lng]).addTo(mapRef.current);
            markerRef.current = newMarker;
          }

          setSearchResults([]);
          setSearchQuery('');
        }
      });
    } catch (error) {
      console.error('Error selecting location:', error);
      setLocationError(`Error selecting location: ${error.message}`);
    }
  };

  // Use useCallback with proper dependencies
  const handleMapClick = useCallback(async (e) => {
    const lat = parseFloat(e.latlng.lat.toFixed(6));
    const lng = parseFloat(e.latlng.lng.toFixed(6));

    if (!mapRef.current) {
      console.error('Map instance not available');
      return;
    }

    if (markerRef.current) {
      mapRef.current.removeLayer(markerRef.current);
    }
    
    const newMarker = L.marker([lat, lng]).addTo(mapRef.current);
    markerRef.current = newMarker;

    if (!geocoderRef.current) return;

    try {
      geocoderRef.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const result = results[0];
            const addressComponents = {};
            
            result.address_components.forEach(component => {
              component.types.forEach(type => {
                addressComponents[type] = component.long_name;
              });
            });

            let city = null;
            const possibleCityNames = [
              addressComponents.locality,
              addressComponents.administrative_area_level_2,
              addressComponents.administrative_area_level_3
            ].filter(Boolean);

            for (const cityName of possibleCityNames) {
              city = getCityByName(cityName) || getCityByName(normalizeCityName(cityName));
              if (city) break;
            }

            let state = null;
            let country = null;
            let missingFields = [];

            if (city) {
              state = FieldState.getStateById(city.state_id);
            }

            if (state) {
              country = FieldCountry.getCountryById(state.country_id);
            } else {
              country = { label: 'India', value: 'India' };
            }

            if (!city) missingFields.push('city');
            if (!state) missingFields.push('state');

            const streetParts = [
              addressComponents.premise,
              addressComponents.street_number,
              addressComponents.route,
              addressComponents.sublocality_level_2,
              addressComponents.sublocality_level_1,
              addressComponents.sublocality
            ].filter(Boolean);

            const selectedData = {
              lat,
              lng,
              street: streetParts.join(', '),
              city,
              state,
              country,
              pin: addressComponents.postal_code || '',
              address: result.formatted_address || `Lat: ${lat}, Lng: ${lng}`
            };

            setSelectedPosition(selectedData);

            if (missingFields.length > 0) {
              setLocationError(`Could not find matching ${missingFields.join(', ')} in database. Please select manually.`);
            } else {
              setLocationError('');
            }

            onValueChange(id, selectedData);
          }
        }
      );
    } catch (error) {
      console.error('Reverse geocode error:', error);
    }
  }, [id, onValueChange]);

  useEffect(() => {
    if (!showModal || !googleMapsLoaded) return;

    const mapContainer = document.getElementById('location-map-container');
    if (!mapContainer) return;

    // FIX: Check if map already exists and remove it first
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch (e) {
        console.error('Error removing existing map:', e);
      }
      mapRef.current = null;
      markerRef.current = null;
    }

    // FIX: Also check if container has _leaflet_id (means Leaflet thinks it's in use)
    if (mapContainer._leaflet_id) {
      delete mapContainer._leaflet_id;
    }

    const center = selectedPosition || { lat: 20.5937, lng: 78.9629 };
    const zoom = selectedPosition ? 13 : 5;

    const map = L.map('location-map-container').setView([center.lat, center.lng], zoom);
    mapRef.current = map;

    const googleSat = L.tileLayer('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      attribution: '© Google'
    });

    const googleStreets = L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      attribution: '© Google'
    });

    const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      attribution: '© Google'
    });

    googleStreets.addTo(map);

    const baseMaps = {
      "Google Hybrid": googleHybrid,
      "Google Streets": googleStreets,
      "Google Satellite": googleSat
    };

    L.control.layers(baseMaps).addTo(map);

    if (selectedPosition) {
      const currentMarker = L.marker([selectedPosition.lat, selectedPosition.lng]).addTo(map);
      markerRef.current = currentMarker;
    }

    map.on('click', handleMapClick);

    // Initialize PlacesService for the map
    if (window.google && window.google.maps) {
      const dummyDiv = document.createElement('div');
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
    }

    return () => {
      if (map) {
        try {
          map.off('click', handleMapClick);
          map.remove();
        } catch (e) {
          console.error('Error cleaning up map:', e);
        }
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [showModal, googleMapsLoaded, handleMapClick, selectedPosition]);

  const handleModalClose = () => {
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch (e) {
        console.error('Error removing map on close:', e);
      }
      mapRef.current = null;
      markerRef.current = null;
    }
    setShowModal(false);
    setSearchQuery('');
    setSearchResults([]);
    setLocationError('');
  };

  const handleModalConfirm = () => {
    if (!selectedPosition) {
      alert('Please select a location on the map first.');
      return;
    }
    
    const locationData = {
      lat: selectedPosition.lat,
      lng: selectedPosition.lng,
      street: selectedPosition.street || '',
      city: selectedPosition.city || null,
      state: selectedPosition.state || null,
      country: selectedPosition.country || null,
      pin: selectedPosition.pin || '',
      address: selectedPosition.address || ''
    };
    
    onValueChange(id, locationData);

    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch (e) {
        console.error('Error removing map on confirm:', e);
      }
      mapRef.current = null;
      markerRef.current = null;
    }
    
    setShowModal(false);
    setSearchQuery('');
    setSearchResults([]);
    setLocationError('');
  };

  if (!visible) return null;

  const label = field?.label || field?.field_label || 'Location';
  
  return (
    <div className="col">
      <div className="form-group">
        <label>{label}</label>
        <InputGroup style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
          <InputGroup.Text 
            onClick={() => editable && setShowModal(true)} 
            style={{ cursor: editable ? 'pointer' : 'default', background: '#f0f2f5' }}
          >
            <FaMapMarkerAlt color={editable ? '#007bff' : '#6c757d'} />
          </InputGroup.Text>
          <FormControl
            value={selectedPosition?.address || ''}
            placeholder="Click to select location"
            readOnly
            onClick={() => editable && setShowModal(true)}
            style={{ cursor: editable ? 'pointer' : 'default' }}
          />
        </InputGroup>
        {selectedPosition?.address && (
          <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
            📍 {selectedPosition.address}
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
        <Modal.Body style={{ padding: 0, position: 'relative', height: '550px' }}>
          
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 1100,
            width: '320px',
            background: '#fff',
            padding: '8px 10px',
            borderRadius: '8px',
            boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
          }}>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <FormControl 
                placeholder="Search location (min 3 chars)..." 
                value={searchQuery} 
                onChange={handleSearchChange} 
              />
            </InputGroup>

            {isSearching && (
              <div style={{ padding: '8px', textAlign: 'center', color: '#666', fontSize: '0.875rem' }}>
                <Spinner animation="border" size="sm" className="me-2" />
                Searching...
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '8px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
                {searchResults.map((prediction, i) => (
                  <div 
                    key={prediction.place_id} 
                    onClick={() => handleSelectSearchResult(prediction)}
                    style={{ 
                      padding: '10px', 
                      cursor: 'pointer', 
                      borderBottom: i < searchResults.length - 1 ? '1px solid #eee' : 'none',
                      fontSize: '0.875rem',
                      transition: 'background-color 0.2s' 
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f3f5'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                  >
                    <div style={{ fontWeight: '500' }}>
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery.length >= 3 && !isSearching && searchResults.length === 0 && (
              <div style={{ 
                padding: '8px', 
                textAlign: 'center', 
                color: '#666',
                fontSize: '0.875rem',
                marginTop: '8px',
                background: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                🔍 No locations found. Try a different search term.
              </div>
            )}
          </div>
          
          <div id="location-map-container" style={{ height: '100%', width: '100%' }}></div>
          
          {locationError && (
            <div style={{ position: 'absolute', top: '60px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100, width: '90%', maxWidth: '400px' }}>
              <Alert variant="warning" className="mb-0" style={{ fontSize: '0.875rem' }}>
                ⚠️ {locationError}
              </Alert>
            </div>
          )}

          {selectedPosition && (
            <div style={{ position: 'absolute', bottom: '50px', left: '10px', zIndex: 1100, background: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 3px 6px rgba(0,0,0,0.15)', maxWidth: '400px', fontSize: '0.875rem' }}>
              <div style={{ fontWeight: '500', marginBottom: '6px' }}>📍 Selected Location:</div>
              {selectedPosition.address && <div>{selectedPosition.address}</div>}
              {selectedPosition.street && <div>🏠 Street: {selectedPosition.street}</div>}
              {selectedPosition.city && <div>🏙️ City: {selectedPosition.city.label}</div>}
              {selectedPosition.state && <div>🗺️ State: {selectedPosition.state.label}</div>}
              {selectedPosition.country && <div>🌍 Country: {selectedPosition.country.label}</div>}
              {selectedPosition.pin && <div>📮 PIN: {selectedPosition.pin}</div>}
            </div>
          )}

          <div style={{ position: 'absolute', top: '60px', right: '10px', zIndex: 1100, background: 'rgba(255,255,255,0.9)', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 3px 6px rgba(0,0,0,0.2)', maxWidth: '200px', fontSize: '0.8rem', color: '#666'}}>
            💡 Click on map or search above
          </div>
          
          <div style={{ position: 'absolute', bottom: '10px', right: '10px', zIndex: 1100, display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.9)', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 3px 6px rgba(0,0,0,0.2)' }}>
            <Button size="sm" variant="secondary" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button 
              size="sm" 
              variant="primary" 
              onClick={handleModalConfirm} 
              disabled={!selectedPosition}
            >
              Save Location
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FieldLocation;